import { Box, Stack } from '@mui/material';
import { AxiosError } from 'axios';
import { Button, ITableHeadColumn, Table, Typography } from 'components';
import { TextField } from 'components/Form';
import { Formik } from 'formik';
import { overflow } from 'html2canvas/dist/types/css/property-descriptors/overflow';
import React, { useMemo } from 'react';
import {
  HTTPValidationError,
  ModulesTrainingActivitiesSchemasActivitySchema,
  ModulesTrainingAnalysisSchemasWorkflow,
  Workflow,
  WorkflowDependencyItem
} from 'schemas';
import * as Yup from 'yup';

interface IProps {
  config_id: string;
  camera_length: number;
  activities?: ModulesTrainingActivitiesSchemasActivitySchema[];
  workflow_data: Workflow[];
  labelsLength: number;
  framesAnnotated: number;
  onSumbit: (model_name: string, workflow_name: string, epochs: number) => void;
  loading?: boolean;
  error?: AxiosError<HTTPValidationError, any> | null;
  trainingStarted?: boolean;
  framesExtracted?: number;
}

interface FormValues {
  model_name: string;
  workflow_name: string;
  config_id: string;
  epochs: number;
}

const TrainSummaryModal: React.FC<IProps> = ({
  config_id,
  activities,
  camera_length,
  workflow_data,
  onSumbit,
  labelsLength,
  error,
  trainingStarted,
  framesAnnotated,
  framesExtracted
}) => {
  const initialValues: FormValues = {
    model_name: '',
    workflow_name: '',
    config_id: config_id,
    epochs: 300
  };

  console.log('activities inside train settings', framesExtracted);

  const validationSchema = Yup.object().shape({
    model_name: Yup.string()
      .required('Model Name is required')
      .min(4, 'Name must be min. 4 characters')
      .max(15, 'Should not exceed 15 characters')
      .matches(/^[^\s]/, 'Name cannot start with a space') // Prevents space at the start
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9_\- ]*$/,
        'letter, num, hyphen, _ are allowed'
      ),
    workflow_name: Yup.string()
      .required('Workflow Name is required')
      .min(4, 'Name must be min. 4 characters')
      .max(15, 'Should Not exceed 15 characters')
      .matches(/^[^\s]/, 'Name cannot start with a space') // Prevents space at the start
      .matches(
        /^[a-zA-Z0-9][a-zA-Z0-9_\- ]*$/,
        'letter, num, hyphen, _ are allowed'
      ),
    config_id: Yup.string().required('Config ID is required'),
    epochs: Yup.number()
      .min(1, 'Learning Cycles Must be greater than 0')
      .max(500, 'Learning Cycles be less than 500')
      .typeError('Learning Cycles must be a number')
      .required('Learning Cycles is required')
  });

  const handleSubmit = (values: FormValues) => {
    console.log('triggered handlesubmit');
    onSumbit(values.model_name, values.workflow_name, values.epochs);
  };

  return (
    <Box>
      {trainingStarted ? (
        <>
          <Box my={4}>
            <Typography
              variant="h6"
              color="primary"
              textAlign={'center'}
              style={{
                fontWeight: 'bold'
              }}
            >
              Model training has started!
            </Typography>
          </Box>
        </>
      ) : (
        <>
          <Formik<FormValues>
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <div>
                  <table width={'100%'} className="modal-summary-table">
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          N of Videos Uploaded
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          {camera_length}
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          Total Images Extracted
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          {framesExtracted}
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          N of Images annotated
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          {framesAnnotated}
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          N of labels annotated
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          {labelsLength}
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          Model Name
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          <TextField
                            name="model_name"
                            type="text"
                            variant="outlined"
                            size="small"
                          />
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 'bold'
                          }}
                        >
                          Workflow Name
                        </Typography>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          <TextField
                            name="workflow_name"
                            type="text"
                            variant="outlined"
                            size="small"
                          />
                        </Typography>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div style={{ display: 'flex' }}>
                          {' '}
                          <Typography
                            variant="subtitle2"
                            style={{
                              fontWeight: 'bold'
                            }}
                          >
                            Learning Cycles
                          </Typography>
                        </div>
                      </td>
                      <td>
                        <Typography variant="subtitle2">
                          <TextField
                            name="epochs"
                            type="number"
                            variant="outlined"
                            size="small"
                            onKeyDown={(e: any) => {
                              if (e.key === ' ') {
                                e.preventDefault();
                              }
                            }}
                          />
                        </Typography>
                      </td>
                    </tr>
                  </table>
                </div>
                {/* <Box mt={2}>
                  <Table
                    columns={columns}
                    rows={workflow_data.map((workflow, index) => {
                      return {
                        activity:
                          activitiesMap[workflow.activity_id]?.activity || '',
                        dependency: workflow.dependency
                          ? activitiesMap[workflow.dependency]?.activity || ''
                          : 'Na'
                      };
                    })}
                    striped
                  />
                </Box> */}
                {error && (
                  <Typography variant="subtitle2" color="error">
                    {error.message || ''}
                  </Typography>
                )}
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  mt={3}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    style={{ backgroundColor: '#295D65' }}
                  >
                    Start Training
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </>
      )}
    </Box>
  );
};

export default TrainSummaryModal;
